import React, { useMemo } from 'react';
import { Alert, Box, Chip, Divider, Paper, Typography } from '@mui/material';
import { guidanceContextForVisit } from '../../clinical/visitGuidelineRegistry';
import { moduleLabels } from '../../clinical/visitSectionModules';

const FALLBACK_HELP = {
  history: 'No module-specific history prompt is active yet. Use the presenting complaints, service pathway and clinical findings to guide the history.',
  examination: 'No module-specific examination priority is active yet. Complete the clinically relevant general and focused examination.',
  investigations: 'No module-specific investigation priority is active yet. Order tests only when clinically indicated.',
  prescription: 'No module-specific treatment priority is active yet. Base treatment on the working diagnosis and reviewed medicine-safety information.',
};

export default function ClinicalGuidancePanel({ visit, section }) {
  const context = useMemo(() => guidanceContextForVisit(visit, section), [visit, section]);
  const modules = useMemo(() => moduleLabels(context.modules), [context.modules]);
  const reason = [
    context.serviceArea ? `service: ${context.serviceArea}` : '',
    context.complaints.length ? `complaints: ${context.complaints.join(', ')}` : '',
    context.triagePriority ? `triage: ${context.triagePriority}` : '',
  ].filter(Boolean).join(' • ');

  return (
    <Paper variant="outlined" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 16 } }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Clinical Guidance</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {context.sectionLabel}
      </Typography>

      {context.pathway ? <Chip size="small" label={`Pathway: ${context.pathway}`} sx={{ mb: 1 }} /> : null}

      {reason ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
          Why shown: {reason}
        </Typography>
      ) : null}

      <Alert severity="info" sx={{ mb: 1.5 }}>
        {context.prompts.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            {context.prompts.slice(0, 6).map((prompt) => (
              <Box component="li" key={prompt} sx={{ mb: 0.5 }}>{prompt}</Box>
            ))}
          </Box>
        ) : (FALLBACK_HELP[section] || FALLBACK_HELP.history)}
      </Alert>

      {modules.length ? (
        <>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>Active reusable modules</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
            {modules.map((item) => <Chip key={item.key} size="small" variant="outlined" label={item.label} />)}
          </Box>
        </>
      ) : null}

      <Divider sx={{ my: 1.25 }} />
      <Typography variant="caption" color="text.secondary" display="block">
        Rule pack: {context.rulePackId} · {context.rulePackVersion} · {context.rulePackStatus}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Source status: {context.sourceStatus}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
        Decision support only. Source/rule updates must be staged, reviewed, tested and explicitly activated; they must never silently alter patient-care logic.
      </Typography>
    </Paper>
  );
}
